import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateTenant } from '../../api/hooks';
import { FormActions, FormRow, TagsInput } from '../../components/Form';
import { Panel } from '../../components/Panel';
import { errorMessage } from '../../components/States';
import { useToast } from '../../components/Toast';
import { PageHeader } from '../../layout/PageHeader';
import { slugify } from '../../lib/format';

export function TenantFormPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateTenant();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [timezone, setTimezone] = useState('Australia/Melbourne');
  const [tags, setTags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(name);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const tenant = await create.mutateAsync({
        name: name.trim(),
        slug: effectiveSlug || undefined,
        contactName: contactName.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        timezone: timezone.trim() || undefined,
        tags,
        notes: notes.trim() || undefined,
      });
      toast.push(`Tenant "${tenant.name}" created`, 'success');
      navigate(`/t/${tenant.tenantId}/integrations`);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      <PageHeader title="Create tenant" subtitle="A tenant is one client organisation. Its users only ever see this tenant." crumbs={[{ label: 'MSP portal', to: '/msp' }, { label: 'Create tenant' }]} />
      <form onSubmit={submit}>
        <Panel title="Tenant details">
          <div className="form">
            {error ? <div className="form-error">{error}</div> : null}
            <FormRow label="Name" required htmlFor="t-name">
              <input id="t-name" className="input" required minLength={2} maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
            </FormRow>
            <FormRow label="Slug" htmlFor="t-slug" hint="Short identifier used in URLs. Lowercase letters, digits and dashes.">
              <input id="t-slug" className="input mono" value={effectiveSlug} pattern="[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?" onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }} />
            </FormRow>
            <FormRow label="Primary contact" htmlFor="t-contact">
              <input id="t-contact" className="input" value={contactName} onChange={(e) => setContactName(e.target.value)} />
            </FormRow>
            <FormRow label="Contact email" htmlFor="t-email" hint="Not an invitation. Invite users from the tenant's Administrators page.">
              <input id="t-email" className="input" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            </FormRow>
            <FormRow label="Time zone" htmlFor="t-tz">
              <input id="t-tz" className="input" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
            </FormRow>
            <FormRow label="Tags" htmlFor="t-tags">
              <TagsInput value={tags} onChange={setTags} />
            </FormRow>
            <FormRow label="Notes" htmlFor="t-notes">
              <textarea id="t-notes" className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </FormRow>
            <FormActions>
              <button type="button" className="btn" onClick={() => navigate('/msp')}>Cancel</button>
              <button type="submit" className="btn btn--primary" disabled={create.isPending}>{create.isPending ? 'Creating…' : 'Create tenant'}</button>
            </FormActions>
          </div>
        </Panel>
      </form>
    </>
  );
}
